import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getTelegramToken, resetPassword, sendTestEmail } from '../services/auth';
import './Account.css';

const DEVICE_CATEGORIES = ['OW', 'TI', 'ALC', 'RA', 'SIS'];
const DEVICE_CATEGORY_LABELS = {
    OW: 'OW 水位觀測井',
    TI: 'TI 傾斜計',
    ALC: 'ALC 地錨荷重計',
    RA: 'RA 雨量監測計',
    SIS: 'SIS 坍方偵測器',
};

const normalizeCategoryKey = (key = '') => {
    if (!key) return null;
    const upperKey = key.toString().trim().toUpperCase();
    if (DEVICE_CATEGORIES.includes(upperKey)) return upperKey;
    const mapping = {
        WATER_LEVEL: 'OW',
        FORCE: 'ALC',
        LOAD: 'ALC',
        RAIN: 'RA',
        RAINFALL: 'RA',
        LS: 'SIS',
        LANDSLIDE: 'SIS',
    };
    return mapping[upperKey] || null;
};

const SLOPE_CATEGORY_KEYS = {
    OW: ['water_level', 'ow'],
    TI: ['tilt', 'ti'],
    ALC: ['force', 'alc'],
    RA: ['rain', 'ra', 'rainfall'],
    SIS: ['landslide', 'ls', 'sis'],
};

const pickDeviceNames = (list) => {
    if (!Array.isArray(list)) return [];
    return list
        .map((item) => {
            if (typeof item === 'string') return item;
            return item?.name || item?.device_name || item?.deviceName || null;
        })
        .filter(Boolean);
};

const deriveDevicesFromSlopeData = (slopeData) => {
    const buckets = DEVICE_CATEGORIES.reduce((acc, key) => {
        acc[key] = [];
        return acc;
    }, {});

    if (!slopeData || typeof slopeData !== 'object') return buckets;

    DEVICE_CATEGORIES.forEach((category) => {
        const keys = SLOPE_CATEGORY_KEYS[category] || [];
        const names = keys.flatMap((key) => pickDeviceNames(slopeData?.[key]));
        buckets[category] = Array.from(new Set(names));
    });

    return buckets;
};

const deriveDevicesByCategory = (user) => {
    const buckets = DEVICE_CATEGORIES.reduce((acc, key) => {
        acc[key] = [];
        return acc;
    }, {});

    if (!user) return buckets;

    const pushNames = (key, names = []) => {
        const normalizedKey = normalizeCategoryKey(key);
        if (!normalizedKey) return;
        const existing = buckets[normalizedKey] || [];
        const merged = [
            ...existing,
            ...names.filter((name) => !!name && typeof name === 'string'),
        ];
        buckets[normalizedKey] = Array.from(new Set(merged));
    };

    const objectSources = [
        user.devicePermissions,
        user.device_permissions,
        user.devices_by_type,
        user.instrumentPermissions,
        user.instruments_by_type,
        user.allowedDevicesByType,
    ].filter((src) => src && typeof src === 'object' && !Array.isArray(src));

    objectSources.forEach((source) => {
        Object.entries(source).forEach(([rawKey, value]) => {
            if (Array.isArray(value)) {
                pushNames(rawKey, value);
                return;
            }
            if (value && typeof value === 'object') {
                if (Array.isArray(value.names)) {
                    pushNames(rawKey, value.names);
                } else if (Array.isArray(value.list)) {
                    pushNames(rawKey, value.list);
                } else if (Array.isArray(value.devices)) {
                    pushNames(
                        rawKey,
                        value.devices.map((device) => device?.name || device)
                    );
                }
            }
        });
    });

    const arraySources = [
        user.devices,
        user.allowedDevices,
        user.instruments,
        user.device_list,
    ].filter((src) => Array.isArray(src));

    arraySources.forEach((list) => {
        list.forEach((item = {}) => {
            if (typeof item === 'string') return;
            const type =
                item.category ||
                item.type ||
                item.device_type ||
                item.deviceType ||
                item.kind ||
                '';
            const name = item.name || item.device_name || item.deviceName;
            pushNames(type, name ? [name] : []);
        });
    });

    return buckets;
};

const displayValue = (value) => {
    if (value == null) return '尚無資料';
    const text = `${value}`.trim();
    return text || '尚無資料';
};

const deriveTelegramTokenFromUser = (user) =>
    user?.telegram_token || user?.telegramToken || user?.telegramTokenValue || '';

const extractTelegramTokenFromPayload = (payload) => {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;
    if (typeof payload === 'object') {
        return (
            payload.message ||
            payload.token ||
            payload.telegram_token ||
            payload.telegramToken ||
            payload.data?.token ||
            payload.data?.telegram_token ||
            payload.data?.telegramToken ||
            ''
        );
    }
    return '';
};

const Account = () => {
    const { user } = useAuth();
    const { slopeData } = useOutletContext() || {};
    const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
    const [hasCopiedToken, setHasCopiedToken] = useState(false);
    const [activeDeviceCategory, setActiveDeviceCategory] = useState(
        DEVICE_CATEGORIES[0]
    );
    const [isMobileLayout, setIsMobileLayout] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.innerWidth <= 768;
    });
    const [telegramToken, setTelegramToken] = useState(() =>
        deriveTelegramTokenFromUser(user)
    );
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const [resetFeedback, setResetFeedback] = useState(null);
    const [confirmPassword, setConfirmPassword] = useState('');

    const email = user?.email || user?.account || user?.username || '尚無資料';
    const name = user?.name || user?.display_name || '尚無資料';
    const role = user?.role || user?.role_name || user?.roleName || '尚無資料';
    const slope = user?.slope?.name || user?.slope_name || user?.slope || '尚無資料';
    const institution =
        user?.institution || user?.institution_name || user?.unit || '尚無資料';

    useEffect(() => {
        setTelegramToken(deriveTelegramTokenFromUser(user));
    }, [user]);

    useEffect(() => {
        const controller = new AbortController();

        const fetchTelegramToken = async () => {
            try {
                const payload = await getTelegramToken(controller.signal);
                const token = extractTelegramTokenFromPayload(payload);
                if (token && token === 'Telegram chat already linked') {
                    setTelegramToken(token);
                } else {
                    const command = '/token=' + token;
                    if (token) {
                        setTelegramToken(command);
                    }
                }
            } catch (error) {
                if (error?.name === 'CanceledError' || error?.message === 'canceled') {
                    return;
                }
                console.error('Failed to fetch telegram token:', error);
            }
        };

        fetchTelegramToken();

        return () => controller.abort();
    }, [user]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handleResize = () => {
            setIsMobileLayout(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const slopeDerivedDevices = useMemo(
        () => deriveDevicesFromSlopeData(slopeData),
        [slopeData]
    );

    const userDerivedDevices = useMemo(() => deriveDevicesByCategory(user), [user]);

    const categorizedDevices = useMemo(() => {
        const slopeHasData = Object.values(slopeDerivedDevices).some(
            (list) => list.length > 0
        );
        if (slopeHasData) return slopeDerivedDevices;
        return userDerivedDevices;
    }, [slopeDerivedDevices, userDerivedDevices]);

    const hasDevicePermissions = useMemo(
        () => Object.values(categorizedDevices).some((list) => list.length > 0),
        [categorizedDevices]
    );

    const handleSendTestEmail = async () => {
        if (!user?.email) {
            alert('目前沒有可用的電子郵件信箱');
            return;
        }
        setIsSendingTestEmail(true);
        try {
            await sendTestEmail();
            alert('測試郵件已發送，請稍後查看您的信箱');
        } finally {
            setIsSendingTestEmail(false);
        }
    };

    const handleCopyToken = async () => {
        if (!telegramToken) {
            alert('尚未取得 telegram token');
            return;
        }
        try {
            if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(telegramToken);
                setHasCopiedToken(true);
                setTimeout(() => setHasCopiedToken(false), 2000);
            } else {
                throw new Error('Clipboard API not available');
            }
        } catch (error) {
            console.error('Failed to copy telegram token:', error);
            alert('複製失敗，請手動選取複製');
        }
    };

    const handleResetPassword = async (event) => {
        event.preventDefault();
        setResetFeedback(null);

        if (!currentPassword || !newPassword || !confirmPassword) {
            setResetFeedback({
                type: 'error',
                text: '請完整填寫目前密碼與新密碼。',
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            setResetFeedback({
                type: 'error',
                text: '兩次輸入的新密碼不一致，請確認後再試。',
            });
            return;
        }

        setIsResettingPassword(true);
        try {
            await resetPassword(currentPassword, newPassword);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setResetFeedback({
                type: 'success',
                text: '密碼已更新，請使用新密碼登入。',
            });
        } catch (error) {
            setResetFeedback({
                type: 'error',
                text: error?.message || '密碼更新失敗，請稍後再試。',
            });
        } finally {
            setIsResettingPassword(false);
        }
    };

    return (
        <div className="account-page">
            <header className="account-page__header">
                <div>
                    <h1>帳號資訊</h1>
                </div>
            </header>

            <div className="account-grid">
                <section className="account-card">
                    <div className="account-card__header">
                        <h2>Telegram 設定</h2>
                        <p>將 token 傳給 Telegram 機器人即可開通通知。</p>
                        <div className="account-steps">
                            <div className="account-step">
                                <span className="account-step__dot" />
                                <div>
                                    <div className="account-step__title">
                                        1. 註冊 Telegram
                                    </div>
                                    <a
                                        href="https://telegram.org/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="account-step__link"
                                    >
                                        https://telegram.org/
                                    </a>
                                </div>
                            </div>
                            <div className="account-step">
                                <span className="account-step__dot" />
                                <div>
                                    <div className="account-step__title">
                                        2. 打開機器人
                                    </div>
                                    <a
                                        href={import.meta.env.VITE_TG_BOT_LINK}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="account-step__link"
                                    >
                                        {import.meta.env.VITE_TG_BOT_LINK}
                                    </a>
                                </div>
                            </div>
                            <div className="account-step">
                                <span className="account-step__dot" />
                                <div>
                                    <div className="account-step__title">
                                        3. 傳送下方 token 到機器人聊天室
                                    </div>
                                    <div className="account-step__desc">
                                        直接貼上即可完成綁定
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="account-token">
                        <span className="account-token__value">
                            {telegramToken || '尚未取得 token'}
                        </span>
                        <button
                            className="account-btn account-btn--secondary"
                            type="button"
                            onClick={handleCopyToken}
                            disabled={
                                !telegramToken ||
                                telegramToken === 'Telegram chat already linked'
                            }
                        >
                            {hasCopiedToken ? '已複製' : '複製'}
                        </button>
                    </div>
                </section>

                <section className="account-card">
                    <div className="account-card__header">
                        <h2>變更密碼</h2>
                        <p>更新登入密碼，建議使用至少 8 碼的新密碼。</p>
                    </div>
                    <form className="account-form" onSubmit={handleResetPassword}>
                        <label
                            className="account-input"
                            htmlFor="account-current-password"
                        >
                            <span className="account-input__label">目前密碼</span>
                            <input
                                id="account-current-password"
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder="輸入目前密碼"
                                autoComplete="current-password"
                                required
                            />
                        </label>
                        <label className="account-input" htmlFor="account-new-password">
                            <span className="account-input__label">新密碼</span>
                            <input
                                id="account-new-password"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="輸入新密碼"
                                autoComplete="new-password"
                                required
                            />
                        </label>
                        <label
                            className="account-input"
                            htmlFor="account-confirm-password"
                        >
                            <span className="account-input__label">確認新密碼</span>
                            <input
                                id="account-confirm-password"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="再次輸入新密碼"
                                autoComplete="new-password"
                                required
                            />
                        </label>
                        {resetFeedback?.text && (
                            <div
                                className={`account-feedback account-feedback--${
                                    resetFeedback.type === 'success' ? 'success' : 'error'
                                }`}
                            >
                                {resetFeedback.text}
                            </div>
                        )}
                        <button
                            className="account-btn"
                            type="submit"
                            disabled={isResettingPassword}
                        >
                            {isResettingPassword ? '更新中...' : '更新密碼'}
                        </button>
                    </form>
                </section>

                <section className="account-card">
                    <div className="account-card__header">
                        <h2>基本資料</h2>
                        <p>顯示目前登入帳號的基本資訊。</p>
                    </div>
                    <div className="account-field account-field--stacked">
                        <div>
                            <span className="account-field__label">電子郵件</span>
                            <div className="account-field__value">{email}</div>
                        </div>
                        <button
                            className="account-btn account-btn--inline"
                            type="button"
                            onClick={handleSendTestEmail}
                            disabled={isSendingTestEmail || !user?.email}
                        >
                            {isSendingTestEmail ? '測試中...' : '測試郵件發送'}
                        </button>
                    </div>
                    <div className="account-field">
                        <span className="account-field__label">姓名</span>
                        <span className="account-field__value">{displayValue(name)}</span>
                    </div>
                    <div className="account-field">
                        <span className="account-field__label">角色</span>
                        <span className="account-field__value">{displayValue(role)}</span>
                    </div>
                    <div className="account-field">
                        <span className="account-field__label">Slope</span>
                        <span className="account-field__value">
                            {displayValue(slope)}
                        </span>
                    </div>
                    <div className="account-field">
                        <span className="account-field__label">單位</span>
                        <span className="account-field__value">
                            {displayValue(institution)}
                        </span>
                    </div>
                </section>
            </div>

            <section className="account-card account-card--full">
                <div className="account-card__header">
                    <h2>&nbsp;儀器列表</h2>
                </div>
                {hasDevicePermissions ? (
                    <>
                        {!isMobileLayout && (
                            <div className="device-grid device-grid--desktop">
                                {DEVICE_CATEGORIES.map((category) => {
                                    const list = categorizedDevices[category] || [];
                                    return (
                                        <div key={category} className="device-group">
                                            <div className="device-group__header">
                                                <span className="device-group__label">
                                                    {DEVICE_CATEGORY_LABELS[category] ||
                                                        category}
                                                </span>
                                                <span className="device-group__count">
                                                    {/* {list.length} */}
                                                </span>
                                            </div>
                                            {list.length > 0 ? (
                                                <div className="account-device-list">
                                                    {list.map((device) => (
                                                        <div
                                                            key={`${category}-${device}`}
                                                            className="account-device-row"
                                                        >
                                                            {device}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="account-device-group__empty">
                                                    尚無資料
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {isMobileLayout && (
                            <div className="device-mobile">
                                <select
                                    className="device-select"
                                    value={activeDeviceCategory}
                                    onChange={(e) =>
                                        setActiveDeviceCategory(e.target.value)
                                    }
                                >
                                    {DEVICE_CATEGORIES.map((category) => (
                                        <option
                                            key={`option-${category}`}
                                            value={category}
                                        >
                                            {DEVICE_CATEGORY_LABELS[category] || category}
                                        </option>
                                    ))}
                                </select>
                                <div className="device-tab-panel">
                                    <div className="device-group__header">
                                        <span className="device-group__label">
                                            {DEVICE_CATEGORY_LABELS[activeDeviceCategory]}
                                        </span>
                                        <span className="device-group__count">
                                            {/* {(categorizedDevices[activeDeviceCategory] || [])
                                                .length} */}
                                        </span>
                                    </div>
                                    {(categorizedDevices[activeDeviceCategory] || [])
                                        .length > 0 ? (
                                        <div className="account-device-list">
                                            {(
                                                categorizedDevices[
                                                    activeDeviceCategory
                                                ] || []
                                            ).map((device) => (
                                                <div
                                                    key={`${activeDeviceCategory}-${device}`}
                                                    className="account-device-row"
                                                >
                                                    {device}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="account-device-group__empty">
                                            尚無資料
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <p className="account-device-group__empty">
                        尚未取得任何儀器權限資料
                    </p>
                )}
            </section>
        </div>
    );
};

export default Account;
